/**
 * Cart utility functions for the unified cart order flow.
 *
 * All functions are pure — no side effects.
 */

/**
 * Returns the subtotal: sum of (quantity × price) for all cart items.
 * @param {Array} items - Array of CartItem objects
 * @returns {number} subtotal
 */
export function getCartSubtotal(items, beddingPct = 0) {
  if (!items || items.length === 0) return 0;

  // Member bedding discount multiplier (1 for non-members). Applied to EVERY
  // bedding unit, matching the server's _apply_bedding_ringing.
  const beddingDisc = Math.max(0, 1 - (Number(beddingPct) || 0) / 100);

  // Non-bedding lines: sum normally (billed qty × price).
  const nonBedding = items.reduce(
    (sum, item) => (item.isBedding ? sum : sum + billedQuantity(item) * item.price),
    0
  );

  // Bedding lines (option A, mirrors server pricing.py): expand into units;
  // the single highest-priced unit across ALL bedding is full price, every
  // other bedding unit rings at its own item's beddingAdditionalPrice.
  const beddingUnits = [];
  items.forEach((item) => {
    if (!item.isBedding) return;
    const n = Math.max(0, Math.round(item.quantity || 0));
    const add = parseFloat(item.beddingAdditionalPrice != null ? item.beddingAdditionalPrice : 20) || 0;
    for (let k = 0; k < n; k++) beddingUnits.push({ full: item.price, add });
  });
  let bedding = 0;
  if (beddingUnits.length > 0) {
    let firstIdx = 0;
    beddingUnits.forEach((u, idx) => { if (u.full > beddingUnits[firstIdx].full) firstIdx = idx; });
    beddingUnits.forEach((u, idx) => {
      bedding += Math.round((idx === firstIdx ? u.full : u.add) * beddingDisc * 100) / 100;
    });
  }

  return Math.round((nonBedding + bedding) * 100) / 100;
}

/**
 * Returns the quantity to BILL for a cart item. For weight-based items carrying
 * a positive minBillableWeight, the billed quantity is floored at the minimum;
 * otherwise the entered quantity is used. The item only carries minBillableWeight
 * when the tenant has minimum billable weight enabled for this channel (Phase 2).
 * @param {object} item - CartItem
 * @returns {number} billed quantity
 */
export function billedQuantity(item) {
  if (!item) return 0;
  const qty = item.quantity || 0;
  if (item.inputWeight === true && item.minBillableWeight != null) {
    const min = parseFloat(item.minBillableWeight) || 0;
    if (min > 0 && qty < min) return min;
  }
  return qty;
}

/**
 * Returns the total count of items (sum of all quantities).
 * @param {Array} items - Array of CartItem objects
 * @returns {number} total item count
 */
export function getCartItemCount(items) {
  if (!items || items.length === 0) return 0;
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * Returns the number of distinct cart items belonging to a given categoryId.
 * @param {Array} items - Array of CartItem objects
 * @param {string} categoryId - The category ID to filter by
 * @returns {number} count of distinct items in that category
 */
export function getCategoryBadgeCount(items, categoryId) {
  if (!items || items.length === 0) return 0;
  return items.filter(item => item.categoryId === categoryId).length;
}

/**
 * Derives the pricing type from cart items.
 * @param {Array} items - Array of CartItem objects
 * @returns {"per_pound"|"per_item"|"per_bag"|"mixed"} pricing type
 */
export function derivePricingType(items) {
  if (!items || items.length === 0) return "per_bag";
  const allWeight = items.every(item => item.inputWeight === true);
  const allPiece = items.every(item => item.inputWeight === false);
  if (allWeight) return "per_pound";
  if (allPiece) return "per_item";
  return "mixed";
}

/**
 * Returns the total billed weight across all per-pound cart items.
 * Used to price per-pound add-ons (Phase 2c).
 * @param {Array} items - Array of CartItem objects
 * @returns {number} summed quantity of weight-based items
 */
export function getBilledWeight(items) {
  if (!items || items.length === 0) return 0;
  return items
    .filter(item => item.inputWeight === true)
    .reduce((sum, item) => sum + billedQuantity(item), 0);
}

function bagLineQuantity(price, items) {
  const name = String(price.serviceName || "").trim().toLowerCase();
  return (items || []).reduce((count, item) => {
    const sameId = price.serviceId != null && String(item.serviceId) === String(price.serviceId);
    const sameName = name && String(item.serviceName || "").trim().toLowerCase() === name;
    if (!sameId && !sameName) return count;
    return count + (parseFloat(item.quantity) || 0);
  }, 0);
}

/**
 * Returns the total cost of the selected add-ons (Phase 2c).
 * per_pound add-ons are priced on the order's billed weight; per_item on qty.
 * per_bag add-ons use the price saved for each bag service already in the cart.
 * @param {Array} selectedAddons - [{ pricingBasis, unitPrice, quantity, bagPrices }]
 * @param {number} billedWeight - total weight-based quantity for per_pound add-ons
 * @param {Array} items - cart lines, used to count bags for per_bag add-ons
 * @returns {number} add-on subtotal
 */
export function getAddonsTotal(selectedAddons, billedWeight, items) {
  if (!selectedAddons || selectedAddons.length === 0) return 0;
  return selectedAddons.reduce((sum, a) => {
    const price = parseFloat(a.unitPrice) || 0;
    if (a.pricingBasis === "per_pound") {
      return sum + price * (billedWeight || 0);
    }
    if (a.pricingBasis === "per_bag") {
      const bagTotal = (a.bagPrices || []).reduce((bagSum, p) => {
        const unit = parseFloat(p.unitPrice) || 0;
        return bagSum + unit * bagLineQuantity(p, items);
      }, 0);
      return sum + bagTotal;
    }
    const qty = parseFloat(a.quantity) || 0;
    return sum + price * qty;
  }, 0);
}

/**
 * Constructs the order payload for the API.
 * @param {{ items: Array }} cart - Cart state object with items array
 * @param {object} orderDetails - Object with all other order fields
 * @returns {object} Order payload matching API contract
 */
export function buildOrderPayload(cart, orderDetails) {
  const items = cart.items || [];
  // Subtotal may be overridden by the caller when it includes extras the cart
  // items alone don't cover (e.g. Phase 2c add-ons).
  const subtotal = orderDetails.subTotal != null
    ? parseFloat(orderDetails.subTotal)
    : getCartSubtotal(items);
  const subtotalStr = subtotal.toFixed(2);
  const grandTotalStr = orderDetails.grandTotal != null
    ? parseFloat(orderDetails.grandTotal).toFixed(2)
    : subtotalStr;

  return {
    operation: "placeOrder",
    pricingType: derivePricingType(items),
    customerId: orderDetails.customerId,
    laundryId: orderDetails.laundryId,
    address: orderDetails.address,
    doorNumber: orderDetails.doorNumber,
    addressInstructions: orderDetails.addressInstructions,
    specialInstructions: orderDetails.specialInstructions,
    services: items.map(item => ({
      serviceName: item.serviceName,
      weightOrCount: item.quantity,
      servicePrice: item.price,
      inputWeight: item.inputWeight,
      categoryId: item.categoryId,
    })),
    // Phase 2c: selected add-ons. Server re-prices from the catalog by addonId,
    // so only the id + quantity are trusted.
    addons: (orderDetails.addons || []).map(a => ({
      addonId: a.addonId,
      quantity: a.quantity,
    })),
    saveAddonPrefs: orderDetails.saveAddonPrefs || false,
    pickupDate: orderDetails.pickupDate,
    pickupTimeInterval: orderDetails.pickupTimeInterval,
    dropoffDate: orderDetails.dropoffDate,
    dropoffTimeInterval: orderDetails.dropoffTimeInterval,
    dryCleanDropoffDate: orderDetails.dryCleanDropoffDate || null,
    dryCleanDropoffTimeInterval: orderDetails.dryCleanDropoffTimeInterval || null,
    frequency: orderDetails.frequency,
    laundryBags: orderDetails.laundryBags,
    totalCost: subtotalStr,
    subTotal: subtotalStr,
    grandTotal: grandTotalStr,
    tip: orderDetails.tip,
    coupon: orderDetails.coupon || "",
    giftCardCode: orderDetails.giftCardCode || "",
    pickupService: orderDetails.pickupService,
    dropoffService: orderDetails.dropoffService,
    customerPaymentId: orderDetails.customerPaymentId,
    payByInvoice: orderDetails.payByInvoice || false,
  };
}

/**
 * Groups an array of services by their category, preserving category order.
 * Returns an array of { categoryId, categoryName, services: [...] }.
 * Services without a matching category go into an "Uncategorized" group at the end.
 *
 * Supports Property 1: Service grouping preserves all services.
 *
 * @param {Array} services - Array of service objects with categoryId
 * @param {Array} categories - Array of category objects with categoryId, categoryName (defines order)
 * @returns {Array} Array of { categoryId, categoryName, services }
 */
export function groupServicesByCategory(services, categories) {
  if (!services || services.length === 0) return [];
  if (!categories || categories.length === 0) {
    // No categories — put everything in "Uncategorized"
    return [
      {
        categoryId: "uncategorized",
        categoryName: "Uncategorized",
        services: [...services],
      },
    ];
  }

  // Build groups in category order
  const groups = categories.map(cat => ({
    categoryId: cat.categoryId,
    categoryName: cat.categoryName,
    services: [],
  }));

  // Index for fast lookup using Map to avoid prototype key conflicts
  const groupIndex = new Map();
  groups.forEach((group, idx) => {
    groupIndex.set(group.categoryId, idx);
  });

  const uncategorized = [];

  for (const service of services) {
    const idx = groupIndex.get(service.categoryId);
    if (idx !== undefined) {
      groups[idx].services.push(service);
    } else {
      uncategorized.push(service);
    }
  }

  // Filter out empty groups
  const result = groups.filter(g => g.services.length > 0);

  // Append uncategorized at the end if any
  if (uncategorized.length > 0) {
    result.push({
      categoryId: "uncategorized",
      categoryName: "Uncategorized",
      services: uncategorized,
    });
  }

  return result;
}
